import { PageLayout } from '@/global/components';
import { BreadcrumbItem, Breadcrumbs } from '@/lib/Breadcrumbs';
import { TabItem, Tabs } from '@/lib/Tabs';
import { useWorkspace } from '@/features/workspaces/hooks';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { LuLandmark } from 'react-icons/lu';
import RouteConstants from '../router/RouteConstants';
import { UploadedListsTabContainer } from './UploadedLists/UploadedListsTabContainer';
import { NewCommunication } from './NewCommunication/NuovaComunicazione';
import { ModelliTab } from './components/modelli/TemplatesTabContainer';
import { CreditsBadge } from './components/CreditsBadge';
import { COMUNI_TABS_ITEM } from './comuni-helper';
import { ComuniTabLockProvider, useComuniTabLock } from './ComuniTabLockContext';
import { ComuniInboxTab } from './Inbox/ComuniInboxTab';
import ErrorBoundary from '@/components/ErrorBoundary';
import { LIST_POLLING_MS, unreadConversationCount } from '@/features/whatsappInbox/inboxUtils';
import { useWhatsappInboxEnabled } from '@/features/whatsappInbox';
import { useGetWhatsappConversationsQuery } from '@/services/endpoints/whatsappInbox';
import { useWhatsappChannelConnector } from '@/features/connectors/whatsapp';
import { CitizenFormsTab } from './CitizenForms/CitizenFormsTab';

const breadcrumbs: BreadcrumbItem[] = [
  {
    label: 'comuni.breadcrumb',
    path: RouteConstants.comuni,
  },
];

const ComuniTabs: FC = () => {
  const history = useHistory();
  const location = useLocation();
  const workspace = useWorkspace();
  const searchParams = new URLSearchParams(location.search);
  const { tabsLocked } = useComuniTabLock();
  const inboxEnabled = useWhatsappInboxEnabled();
  const { isLoading: isConnectorLoading, businessInstanceId, needsOnboarding } = useWhatsappChannelConnector();
  const connectorReady = !isConnectorLoading && !!businessInstanceId && !needsOnboarding;
  const { data: conversations } = useGetWhatsappConversationsQuery(
    { workspaceId: workspace.id },
    { skip: !inboxEnabled || !connectorReady, pollingInterval: LIST_POLLING_MS }
  );
  const inboxUnreadCount = useMemo(() => unreadConversationCount(conversations ?? []), [conversations]);

  const requestedTab = searchParams.get('tab');
  const currentTab = requestedTab === COMUNI_TABS_ITEM.INBOX && !inboxEnabled ? COMUNI_TABS_ITEM.NEW_COMMUNICATION : requestedTab;

  useEffect(() => {
    if (requestedTab === COMUNI_TABS_ITEM.INBOX && !inboxEnabled) {
      history.replace(`${RouteConstants.comuni}?tab=${COMUNI_TABS_ITEM.NEW_COMMUNICATION}`);
    }
  }, [history, inboxEnabled, requestedTab]);

  const tabItems = useMemo<TabItem[]>(
    () => [
      {
        id: COMUNI_TABS_ITEM.NEW_COMMUNICATION,
        title: `comuni.tabs.${COMUNI_TABS_ITEM.NEW_COMMUNICATION}`,
        element: <NewCommunication />,
      },
      {
        id: COMUNI_TABS_ITEM.UPLOADED_LISTS,
        title: `comuni.tabs.${COMUNI_TABS_ITEM.UPLOADED_LISTS}`,
        element: <UploadedListsTabContainer />,
      },
      {
        id: COMUNI_TABS_ITEM.TEMPLATES,
        title: `comuni.tabs.${COMUNI_TABS_ITEM.TEMPLATES}`,
        element: <ModelliTab />,
      },
      {
        id: COMUNI_TABS_ITEM.CITIZEN_FORMS,
        title: 'comuni.tabs.citizenForms',
        element: <CitizenFormsTab />,
      },
      ...(inboxEnabled
        ? [
            {
              id: COMUNI_TABS_ITEM.INBOX,
              title: 'comuni.tabs.inbox' as const,
              count: inboxUnreadCount,
              element: <ComuniInboxTab />,
            },
          ]
        : []),
    ],
    [inboxEnabled, inboxUnreadCount]
  );

  const handleSelectTab = (tab: TabItem) => {
    if (tabsLocked) return;
    history.push(`${RouteConstants.comuni}?tab=${tab.id}`);
  };

  return (
    <Tabs items={tabItems} currentTab={`comuni.tabs.${currentTab || COMUNI_TABS_ITEM.NEW_COMMUNICATION}`} onSelectTab={handleSelectTab} disabled={tabsLocked} />
  );
};

export const ComuniContainer: FC = () => {
  const { t } = useTranslation('comuni');
  const workspace = useWorkspace();

  return (
    <PageLayout>
      <header className='grid gap-4'>
        <Breadcrumbs items={breadcrumbs} />
        <div className='mb-2 flex items-center gap-4'>
          <span className='flex size-14 flex-none items-center justify-center rounded-full bg-brand-50'>
            <LuLandmark className='size-7 text-brand-700' strokeWidth={1.7} />
          </span>
          <div className='min-w-0 flex-1'>
            <h1 className='text-[32px] font-extrabold leading-tight tracking-tight text-primary-900'>{t('title')}</h1>
            <p className='mt-0.5 text-[15px] text-tertiary-600'>{t('subtitleWithWorkspace', { workspace: workspace.name })}</p>
          </div>
          <ErrorBoundary>
            <CreditsBadge />
          </ErrorBoundary>
        </div>
      </header>
      <ErrorBoundary>
        <ComuniTabLockProvider>
          <ComuniTabs />
        </ComuniTabLockProvider>
      </ErrorBoundary>
    </PageLayout>
  );
};
